import Vue from "vue";
import VueMeta from "vue-meta";
import VueRouter from "vue-router";
import moment from "moment-timezone";

Vue.use(VueMeta);
Vue.use(VueRouter);

const parseFairDayStrings = (fairDays) => {
  if (!fairDays) {
    return [];
  }
  return fairDays
    .split(",")
    .map((d) => d.trim())
    .filter(Boolean);
};

const isFairDay = (fairDays) => {
  const today = moment().format("YYYY-MM-DD");
  const dates = parseFairDayStrings(fairDays);
  if (!dates.length) {
    return false;
  }

  const firstFairDate = dates[0].split("T")[0];

  const fairWindowOpen = (time) => {
    if (time === undefined || time === "") {
      return true;
    }
    return moment().isBefore(moment(time, "HH:mm:ss"));
  };

  let returnValue = false;
  const midday = moment().clone().startOf("day").hour(17).minute(19).second(0);

  dates.forEach((datetime) => {
    const date = datetime.split("T")[0];
    const time = datetime.split("T")[1];
    if (date !== today) {
      return;
    }

    const isFirstFairDay = date === firstFairDate;
    if (isFirstFairDay && moment().isBefore(midday)) {
      return;
    }

    if (fairWindowOpen(time)) {
      returnValue = true;
    }
  });
  return returnValue;
};

const MAX_SET_TIMEOUT_MS = 2147483647;
const POLL_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Single reload after `target` (local clock). Chains timeouts past the browser
 * max delay; optional short poll when the boundary is within 24h.
 */
const registerReloadAfterMoment = (target, reload) => {
  if (!moment().isBefore(target)) {
    return;
  }

  const tick = () => {
    if (!moment().isBefore(target)) {
      reload();
      return;
    }
    const remaining = target.diff(moment());
    const wait = Math.min(Math.max(remaining + 1500, 1), MAX_SET_TIMEOUT_MS);
    setTimeout(tick, wait);
  };
  tick();

  let pollId;
  if (target.diff(moment()) < POLL_WINDOW_MS) {
    pollId = setInterval(() => {
      if (!moment().isBefore(target)) {
        clearInterval(pollId);
        reload();
      }
    }, 15000);
  }

  const onVisible = () => {
    if (document.visibilityState !== "visible") {
      return;
    }
    if (!moment().isBefore(target)) {
      if (pollId) {
        clearInterval(pollId);
      }
      document.removeEventListener("visibilitychange", onVisible);
      reload();
    }
  };
  document.addEventListener("visibilitychange", onVisible);
};

/**
 * Routes are fixed at first load. Reload when:
 * - First fair day: after the configured midday gate (e.g. Online on `/`).
 * - Last fair day entry includes a time: after that instant (fair ended for `/`).
 */
export const scheduleFairDayMiddayReload = () => {
  if (typeof window === "undefined") {
    return;
  }

  const dates = parseFairDayStrings(process.env.VUE_APP_FAIR_DAY);
  if (!dates.length) {
    return;
  }

  let reloaded = false;
  const reload = () => {
    if (reloaded) {
      return;
    }
    reloaded = true;
    window.location.reload();
  };

  const today = moment().format("YYYY-MM-DD");
  const firstFairDate = dates[0].split("T")[0];
  if (today === firstFairDate) {
    const midday = moment().clone().startOf("day").hour(17).minute(19).second(0);
    if (moment().isBefore(midday)) {
      registerReloadAfterMoment(midday, reload);
    }
  }

  const lastRaw = dates[dates.length - 1];
  const lastParts = lastRaw.split("T");
  const lastTime = lastParts[1];
  if (lastTime) {
    const lastDate = lastParts[0];
    const fairEnd = moment(`${lastDate} ${lastTime}`, "YYYY-MM-DD HH:mm:ss");
    if (moment().isBefore(fairEnd)) {
      registerReloadAfterMoment(fairEnd, reload);
    }
  }
};

const routes = [
  isFairDay(process.env.VUE_APP_FAIR_DAY)
    ? {
        path: "/",
        name: "Online",
        component: () =>
          import(/* webpackChunkName: "online" */ "../views/Online.vue"),
      }
    : {
        path: "/",
        name: "Home",
        component: () =>
          import(/* webpackChunkName: "home" */ "../views/Home.vue"),
      },
  {
    path: "/exhibitors",
    name: "Expositor",
    component: () =>
      import(/* webpackChunkName: "exhibitors" */ "../views/Exhibitors.vue"),
  },
  {
    path: "/stands",
    name: "Expositores",
    component: () =>
      import(/* webpackChunkName: "exhibitors" */ "../views/Stands.vue"),
  },
  {
    path: "/welcome",
    name: "La Feria",
    component: () =>
      import(/* webpackChunkName: "welcome" */ "../views/Welcome.vue"),
  },
  {
    path: "/contact",
    name: "Contacto",
    component: () =>
      import(/* webpackChunkName: "welcome" */ "../views/Contact.vue"),
  },
  {
    path: "/history",
    name: "History",
    component: () =>
      import(/* webpackChunkName: "welcome" */ "../views/History.vue"),
    children: [
      {
        path: ":id",
      },
    ],
  },
  {
    path: "/applicants",
    name: "Applicants",
    component: () =>
      import(/* webpackChunkName: "welcome" */ "../views/Applicants.vue"),
    children: [
      {
        path: ":id",
        component: () =>
          import(/* webpackChunkName: "welcome" */ "../views/ApplyForm.vue"),
      },
    ],
  },
  {
    path: "/talks",
    name: "Charlas",
    component: () =>
      import(/* webpackChunkName: "welcome" */ "../views/Talks.vue"),
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

export default router;
